import { useQuery } from "@tanstack/react-query";
import { formatISO } from "date-fns";
import { HTTPError } from "ky";
import * as z from "zod";
import { AccommodationOffer } from "~/applications/OfferSearch/Domain/AccommodationOffer";
import { CancellationPolicyId } from "~/applications/OfferSearch/Domain/CancellationPolicyId";
import { OfferPrices } from "~/applications/OfferSearch/Domain/OfferPrices";
import { OutOfBoundsNumberOfGuests } from "~/applications/OfferSearch/Domain/OutOfBoundsNumberOfGuests";
import { PastStay } from "~/applications/OfferSearch/Domain/PastStay";
import { gogailleApiClient } from "~/clients/GogailleApiClient";
import { Price } from "~/core/Price";
import { PricePayloadSchema } from "~/core/api/commons";
import { ViolationCode, mapViolationCode } from "~/core/api/violationList";

const ErrorPayloadSchema = z.object({
  id: z.preprocess((value: unknown) => {
    if (typeof value !== "string") {
      throw new Error("id must be a string");
    }

    return mapViolationCode(value);
  }, z.nativeEnum(ViolationCode)),
});

const AccommodationOfferPayloadSchema = z.object({
  is_sold_out: z.boolean(),
  pms_unit_group_id: z.string(),
  rate: z.object({
    cancellation_policy_id: z.string(),
    price: z.object({
      price_details: z.object({
        price_per_night: PricePayloadSchema,
        city_tax: PricePayloadSchema,
        total_price: z.unknown(), // Not used
      }),
      total_price: PricePayloadSchema,
      total_price_to_pay: PricePayloadSchema,
    }),
  }),
});

const AccommodationOfferAvailabilityPayloadSchema = z.object({
  accommodation_offers: z.array(AccommodationOfferPayloadSchema),
});

export type AccommodationOfferAvailabilityPayload = z.infer<
  typeof AccommodationOfferAvailabilityPayloadSchema
>;

type FetchOffersQuery = {
  logePmsIds: Array<string>;
  checkIn: Date;
  checkOut: Date;
  nbAdult: number;
  nbChildren: number;
  nbBabies: number;
  promoCode?: string;
  cancellationPolicyId: CancellationPolicyId;
};

export const fetchAccommodationOffersKey = (
  query: FetchOffersQuery,
): ["offer-search-offers", FetchOffersQuery] => ["offer-search-offers", query];

export const fetchOffers = async (
  query: FetchOffersQuery,
): Promise<AccommodationOffer[]> => {
  let response: Response | undefined = undefined;

  try {
    response = await gogailleApiClient.post(
      `${process.env.GOGAILLE_API_ENDPOINT}/api/booking/accommodation_offer_availabilities`,
      {
        headers: {
          accept: "application/json",
          "content-type": "application/json",
        },
        body: JSON.stringify({
          loge_ids: query.logePmsIds,
          checkin: formatISO(query.checkIn, { representation: "date" }),
          checkout: formatISO(query.checkOut, { representation: "date" }),
          nb_adults: query.nbAdult,
          children_ages: [
            ...[...new Array(query.nbChildren)].map(() => 17),
            ...[...new Array(query.nbBabies)].map(() => 2),
          ],
          promo_code: query.promoCode,
          cancellation_policy_id: query.cancellationPolicyId,
        }),
      },
    );
  } catch (err) {
    if (
      err instanceof HTTPError &&
      (err.response.status === 400 || err.response.status === 422)
    ) {
      const payload = await err.response.json();
      const error = ErrorPayloadSchema.parse(payload);

      if (error.id === ViolationCode.PAST_STAY_DATE_ERROR) {
        throw new PastStay();
      }
      if (
        [
          ViolationCode.OUT_OF_BOUNDS_NUMBER_OF_ADULTS_CODE,
          ViolationCode.OUT_OF_BOUNDS_NUMBER_OF_CHILDREN_CODE,
          ViolationCode.OUT_OF_BOUNDS_NUMBER_OF_BABIES_CODE,
        ].includes(error.id)
      ) {
        throw new OutOfBoundsNumberOfGuests();
      }
    }

    throw err;
  }

  const roomAvailabilitiesPayload =
    AccommodationOfferAvailabilityPayloadSchema.parse(await response.json());

  const allOffers = roomAvailabilitiesPayload.accommodation_offers.map(
    (oneAccommodationOffer) => {
      return new AccommodationOffer(
        oneAccommodationOffer.pms_unit_group_id,
        new OfferPrices(
          Price.fromPayload(oneAccommodationOffer.rate.price.total_price),
          Price.fromPayload(
            oneAccommodationOffer.rate.price.total_price_to_pay,
          ),
          Price.fromPayload(
            oneAccommodationOffer.rate.price.price_details.price_per_night,
          ),
          Price.fromPayload(
            oneAccommodationOffer.rate.price.price_details.city_tax,
          ),
        ),
        oneAccommodationOffer.is_sold_out,
      );
    },
  );

  return allOffers;
};

export const useAccommodationOffers = (
  query: FetchOffersQuery | false,
): {
  accommodationOffers: AccommodationOffer[] | null;
} => {
  const { data: accommodationOffers = null } = useQuery(
    query ? fetchAccommodationOffersKey(query) : [],
    () => (query ? fetchOffers(query) : null),
    { enabled: query !== false, useErrorBoundary: true },
  );

  return { accommodationOffers };
};
